import LabResultSignaturePad, { type LabResultSignatureValue } from '@/src/pages/LabTech/LabResultSignaturePad'
import { ADMIN_MODAL_TYPE, useModalStore } from '@/src/zustand/Modal'
import { useConsentForms, usePatient, useProfile } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { consentApprovalSchema } from '@schema'
import { useAppSelector } from '@store/hooks'
import { FormButton, FormInputField, ModalHeader, SelectInputField } from '@ui'
import { getError, showToast } from '@util'
import { Box, CheckBox, Layer, Text } from 'grommet'
import { useEffect } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

type Props = {
  id?: string
  onClose: () => void
  isUpdate?: boolean
  consent_form_id?: string
}

type SignatureProps = {
  label?: string
  name: string
  savedSignature?: string | null
  onSaveToStorage?: (signatureBlob: string) => Promise<void> | void
  onRemoveSavedSignature?: () => Promise<void> | void
  isUpdatingSaved?: boolean
}

export const Signature = ({
  label,
  name,
  savedSignature,
  onSaveToStorage,
  onRemoveSavedSignature,
  isUpdatingSaved
}: SignatureProps) => {
  const {
    setValue,
    watch,
    formState: { errors }
  } = useFormContext()
  const signature = watch(name) as string | undefined
  const errorText = errors[name]?.message
  const displayError = errorText && typeof errorText === 'string' && errorText

  const value: LabResultSignatureValue | null = signature ? { previewUrl: signature, signedId: signature } : null

  return (
    <div className="w-full max-w-md">
      <LabResultSignaturePad
        isUpdatingProfile={isUpdatingSaved}
        label={label || 'Signature'}
        onChange={next => setValue(name, next?.previewUrl || '', { shouldDirty: true, shouldValidate: true })}
        onRemoveProfileSignature={onRemoveSavedSignature}
        onSaveToProfile={onSaveToStorage}
        profileSignature={savedSignature}
        uploadOnSave={false}
        value={value}
      />
      {displayError && (
        <Box>
          <Text color="status-error" size="small">
            {displayError}
          </Text>
        </Box>
      )}
    </div>
  )
}

const Approval = ({ onClose, id, isUpdate, consent_form_id }: Props) => {
  const { closeModal } = useModalStore()
  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()
  const isIntakeConsent = pathname.includes('intake')

  const methods = useForm({
    resolver: yupResolver(consentApprovalSchema(isIntakeConsent))
  })

  useEffect(() => {
    if (isIntakeConsent) {
      methods.unregister('attending_signature')
    }
  }, [isIntakeConsent, methods])

  const { patientId: patient_id } = useParams<string>()
  const { user } = useAppSelector(state => state.user)
  const intake_id = searchParams.get('intake_id') || ''

  const { signee_relationship } = useAppSelector(state => state.config)

  const { updateConsentForm, createConsent } = useConsentForms({ patient_id })
  const { singlePatient, updatePatient, isUpdatingPatient } = usePatient({ id: patient_id })
  const { profile, updateProfileSignature, isUpdatingProfileSignature } = useProfile({
    isLoggedIn: !!user?.authable_id
  })
  console.log(patient_id)

  const handleClose = () => {
    onClose()
    closeModal(ADMIN_MODAL_TYPE.CONSENT_FORM)
  }

  const savePatientSignature = async (signatureBlob: string) => {
    if (!patient_id) return
    await updatePatient({
      id: patient_id,
      formVal: {
        data: {
          attributes: { signature: signatureBlob },
          id: patient_id,
          type: 'patient'
        }
      }
    })
  }

  const removePatientSignature = async () => {
    if (!patient_id) return
    await updatePatient({
      id: patient_id,
      formVal: {
        data: {
          attributes: { signature: '' },
          id: patient_id,
          type: 'patient'
        }
      }
    })
  }

  const onSubmit = async (data: any) => {
    const { signature, attending_signature, ...attributes } = data

    const buildSignatureTag = (src: string) => `<img src="${src}" alt="signature" width="200" height="100" />`

    const payloadAttrs: any = {
      category: 'surgical',
      signature: buildSignatureTag(signature),
      ...attributes
    }

    if (!isIntakeConsent && attending_signature) {
      const sigTag = buildSignatureTag(attending_signature)
      payloadAttrs.attending_signature = sigTag.replace('alt="signature"', 'alt="attending_signature"')
    }

    const payload = {
      data: {
        attributes: payloadAttrs,
        type: 'consent_form'
      }
    }

    const cosentAttrs: any = {
      document_template_id: id,
      signature: buildSignatureTag(signature),
      ...attributes
    }

    if (!isIntakeConsent && attending_signature) {
      const sigTag = buildSignatureTag(attending_signature)
      cosentAttrs.attending_signature = sigTag.replace('alt="signature"', 'alt="attending_signature"')
    }

    const cosentPayload = {
      data: {
        attributes: cosentAttrs,
        relationships: {
          intake: {
            data: {
              id: intake_id,
              type: 'intake'
            }
          },
          patient: {
            data: {
              id: patient_id,
              type: 'patient'
            }
          }
        },
        type: 'consent_form'
      }
    }

    try {
      if (isUpdate) {
        await updateConsentForm({ formVal: cosentPayload, id: consent_form_id || '' })
        handleClose()
        return showToast('Consent form signed successfully')
      } else if (isIntakeConsent) {
        await createConsent(cosentPayload)
        handleClose()
        return showToast('Consent form signed successfully')
      } else {
        await updateConsentForm({ formVal: payload, id: id || '' })
        handleClose()
        return showToast('Consent form signed successfully')
      }
    } catch (error) {
      getError(error)
    }
    handleClose()
  }

  return (
    <FormProvider {...methods}>
      <Layer
        margin={{ vertical: '8px' }}
        modal
        onClickOutside={onClose}
        onEsc={onClose}
        position="center"
        style={{ borderRadius: '15px' }}
      >
        <form className="flex flex-col overflow-auto" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="overflow-auto" style={{ maxHeight: '88vh', width: '47vw' }}>
            <ModalHeader onClose={onClose} title="Consent Approval" />
            <div className="overflow-auto flex flex-col gap-y-4 p-6 h-[600px] ">
              <div>
                <FormInputField label="Signee's full name" name="signed_by" placeholder="Patient/guardian's name" />
              </div>
              <div>
                <SelectInputField
                  label="Relationship to patient"
                  labelKey="name"
                  name="signee_relationship"
                  options={signee_relationship || []}
                  placeholder="E.g father, mother or guardian"
                  valueKey={{ key: 'value', reduce: true }}
                />
              </div>
              <div>
                <Signature
                  isUpdatingSaved={isUpdatingPatient}
                  label="Signee's signature"
                  name="signature"
                  onRemoveSavedSignature={removePatientSignature}
                  onSaveToStorage={savePatientSignature}
                  savedSignature={singlePatient?.attributes?.signature}
                />
              </div>
              {!isIntakeConsent && (
                <div>
                  <Signature
                    isUpdatingSaved={isUpdatingProfileSignature}
                    label="Physician's signature"
                    name="attending_signature"
                    onRemoveSavedSignature={() => updateProfileSignature('')}
                    onSaveToStorage={blob => updateProfileSignature(blob)}
                    savedSignature={profile?.attributes?.signature}
                  />
                </div>
              )}
              <Controller
                control={methods.control}
                defaultValue={false}
                name="is_signed"
                render={({ field, fieldState: { error } }) => (
                  <Box>
                    <Box align="center" direction="row" gap="small">
                      <CheckBox
                        checked={field?.value}
                        label="By checking the box, you are consenting to all the information provided above"
                        onChange={e => {
                          field.onChange(e.target.checked)
                        }}
                      />
                    </Box>
                    <Box pad={{ left: 'medium' }}>
                      <Text color="status-error" size="small">
                        {error?.message}
                      </Text>
                    </Box>
                  </Box>
                )}
              />
            </div>
          </div>
          <FormButton isLoading={methods.formState.isSubmitting} onClose={onClose} submit="Sign" />
        </form>
      </Layer>
    </FormProvider>
  )
}

export default Approval
